import { useState, useEffect, useCallback } from 'react';
import { ticketsApi, locationsApi, companiesApi, devicesApi } from '../../../services/apiClient';
import { useSiteFilter } from '../../../contexts/SiteFilterContext';
import { transformTicketData, transformLocationsForTreeSelect, matchesSearch, wildcardMatch } from '../utils/ticketTransformers';
import { URGENCY_ORDER } from '../utils/ticketConstants';

/**
 * Hook for managing ticket data fetching
 */
export const useTickets = ({ filter, page, rowsPerPage, orderBy, order }) => {
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();

  // State
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reference data
  const [locations, setLocations] = useState([]);
  const [locationsForTreeSelect, setLocationsForTreeSelect] = useState([]);
  const [supportTypes, setSupportTypes] = useState([]);
  const [supportUsers, setSupportUsers] = useState([]);
  const [loadingSupportTypes, setLoadingSupportTypes] = useState(false);
  const [loadingSupportUsers, setLoadingSupportUsers] = useState(false);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params - ensure limit is a valid positive number
      // When searching, use a higher limit to search through more tickets
      const isSearching = filter?.search && filter.search.trim().length > 0;
      const validLimit = isSearching ? 500 : ((rowsPerPage && rowsPerPage > 0) ? rowsPerPage : 50);
      const params = {
        page: isSearching ? 1 : page + 1, // Reset to page 1 when searching
        limit: validLimit
      };

      // Add search parameter for server-side search
      if (isSearching) {
        params.search = filter.search.trim();
      }

      const response = await ticketsApi.getAll(params);

      let ticketsData = [];
      let pagination = null;

      if (response?.data) {
        if (Array.isArray(response.data)) {
          ticketsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          ticketsData = response.data.data;
          pagination = response.data.pagination;
        } else if (response.data.tickets && Array.isArray(response.data.tickets)) {
          ticketsData = response.data.tickets;
          pagination = response.data.pagination;
        }
      }

      let transformedTickets = ticketsData.map(transformTicketData);

      // Debug: log first ticket to see data structure
      if (transformedTickets.length > 0) {
        console.log('[useTickets] Sample ticket data:', {
          id: transformedTickets[0].id,
          location: transformedTickets[0].location,
          locationId: transformedTickets[0].locationId,
          related_device_name: transformedTickets[0].related_device_name,
          createdAt: transformedTickets[0].createdAt
        });
      }

      // Filter by site
      if (hasSiteFilter && siteLocationIds) {
        transformedTickets = transformedTickets.filter(ticket => {
          const ticketLocationId = ticket.locationId || ticket.location?.id;
          return siteLocationIds.includes(Number(ticketLocationId)) || siteLocationIds.includes(String(ticketLocationId));
        });
        console.log(`[Tickets] Filtered to ${transformedTickets.length} tickets for selected site`);
      }

      setTickets(transformedTickets);

      // Set total count
      if (hasSiteFilter && siteLocationIds) {
        setTotalTickets(transformedTickets.length);
      } else if (pagination && pagination.total !== undefined) {
        setTotalTickets(pagination.total);
      } else {
        setTotalTickets(transformedTickets.length);
      }

    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets. Please try again later.');
      setTickets([]);
      setTotalTickets(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, hasSiteFilter, siteLocationIds, filter?.search]);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      const response = await locationsApi.getAll();

      let locationsData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          locationsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          locationsData = response.data.data;
        } else if (response.data.locations && Array.isArray(response.data.locations)) {
          locationsData = response.data.locations;
        }
      }

      const transformedLocations = locationsData.map((loc) => ({
        id: loc.id || loc._id,
        _id: loc._id || loc.id,
        friendlyName: loc.friendlyName || loc.name || `Location ${loc.id || loc._id}`,
        name: loc.name || loc.friendlyName,
        parentId: loc.parentId || loc.parent_id || loc.parentLocation || loc.parent,
        locationType: loc.locationType || loc.location_type || loc.locationTypeModel?.id || loc.locationTypeModel?.value,
        locationTypeModel: loc.locationTypeModel
      }));

      const treeSelectLocations = transformLocationsForTreeSelect(locationsData);

      setLocations(transformedLocations);
      setLocationsForTreeSelect(treeSelectLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
      setLocationsForTreeSelect([]);
    }
  }, []);

  // Fetch support types
  const fetchSupportTypes = useCallback(async () => {
    try {
      setLoadingSupportTypes(true);
      const response = await companiesApi.getSupportTypes();

      let supportTypesData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          supportTypesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          supportTypesData = response.data.data;
        } else if (response.data.supportTypes && Array.isArray(response.data.supportTypes)) {
          supportTypesData = response.data.supportTypes;
        }
      }

      setSupportTypes(supportTypesData);
    } catch (err) {
      console.error('Error fetching support types:', err);
      setSupportTypes([]);
    } finally {
      setLoadingSupportTypes(false);
    }
  }, []);

  // Fetch support users
  const fetchSupportUsers = useCallback(async () => {
    try {
      setLoadingSupportUsers(true);
      const response = await companiesApi.getSupportUsers();

      let usersData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          usersData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          usersData = response.data.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          usersData = response.data.users;
        }
      }

      setSupportUsers(usersData);
    } catch (err) {
      console.error('Error fetching support users:', err);
      setSupportUsers([]);
    } finally {
      setLoadingSupportUsers(false);
    }
  }, []);

  // Fetch devices for a location
  const fetchDevices = useCallback(async (locationId = null) => {
    try {
      let response;
      if (locationId && devicesApi.getByLocation) {
        try {
          response = await devicesApi.getByLocation(locationId);
        } catch {
          response = await devicesApi.getAll();
        }
      } else {
        response = await devicesApi.getAll();
      }

      let devicesData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          devicesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          devicesData = response.data.data;
        } else if (response.data.devices && Array.isArray(response.data.devices)) {
          devicesData = response.data.devices;
        }
      }

      return devicesData.map((device) => ({
        id: device.id || device._id,
        name: device.friendlyName || device.name || `Device ${device.id || device._id}`,
        friendlyName: device.friendlyName || device.name,
        deviceType: device.deviceType || device.device_type,
        locationId: device.deviceLocation || device.locationId || device.location_id || device.location?.id
      }));
    } catch (err) {
      console.error('Error fetching devices:', err);
      return [];
    }
  }, []);

  // Filter tickets (client-side)
  const filteredTickets = tickets.filter((ticket) => {
    const matchesStatus = filter.status.length === 0 || filter.status.includes(ticket.status);
    const matchesUrgency = filter.urgency === '' || ticket.urgency === filter.urgency;
    const matchesSupportType = filter.support_type === '' || ticket.support_type === filter.support_type;
    const matchesNextStep = !filter.next_step ||
      (ticket.next_step && wildcardMatch(ticket.next_step, filter.next_step));
    const matchesSearchTerm = matchesSearch(ticket, filter.search);

    return matchesStatus && matchesUrgency && matchesSupportType && matchesNextStep && matchesSearchTerm;
  });

  // Sort tickets
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      case 'title':
        aValue = (a.title || a.description || '').toLowerCase();
        bValue = (b.title || b.description || '').toLowerCase();
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'urgency':
        aValue = URGENCY_ORDER[a.urgency] || 0;
        bValue = URGENCY_ORDER[b.urgency] || 0;
        break;
      case 'support_type':
        aValue = a.support_type || '';
        bValue = b.support_type || '';
        break;
      case 'next_step':
        aValue = (a.next_step || '').toLowerCase();
        bValue = (b.next_step || '').toLowerCase();
        break;
      case 'location':
        aValue = (a.location?.friendlyName || '').toLowerCase();
        bValue = (b.location?.friendlyName || '').toLowerCase();
        break;
      case 'device':
        aValue = (a.related_device_name || '').toLowerCase();
        bValue = (b.related_device_name || '').toLowerCase();
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
        break;
      case 'updatedAt':
        aValue = new Date(a.updatedAt || 0).getTime();
        bValue = new Date(b.updatedAt || 0).getTime();
        break;
      default:
        aValue = a[orderBy] || '';
        bValue = b[orderBy] || '';
    }

    if (order === 'desc') {
      return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
    }
    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
  });

  // Initial data load
  useEffect(() => {
    fetchTickets();
    fetchLocations();
    fetchSupportTypes();
  }, [fetchTickets, fetchLocations, fetchSupportTypes]);

  // Refetch when site filter changes
  useEffect(() => {
    if (selectedSiteId !== undefined) {
      fetchTickets();
    }
  }, [selectedSiteId, fetchTickets]);

  return {
    // Ticket data
    tickets: sortedTickets,
    totalTickets: filteredTickets.length,
    allTickets: tickets,
    loading,
    error,

    // Reference data
    locations,
    locationsForTreeSelect,
    supportTypes,
    supportUsers,
    loadingSupportTypes,
    loadingSupportUsers,

    // Actions
    fetchTickets,
    fetchLocations,
    fetchSupportTypes,
    fetchSupportUsers,
    fetchDevices,
    setTickets
  };
};

export default useTickets;
